import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useCompany } from "./useCompany";
import { buildColorMap, getOptionColorFromHex } from "@/lib/colorHash";
import { useCallback, useMemo } from "react";

interface OptionColor {
  id: string;
  option_type: string;
  option_value: string;
  color: string;
}

/**
 * Accepts lists of all active values per type so colors are assigned sequentially
 * (maximally distinct) rather than by hash.
 */
export function useOptionColors(activeValues?: {
  vehicle?: string[];
  expense_type?: string[];
  paid_by?: string[];
}) {
  const { user } = useAuth();
  const { company } = useCompany();
  const queryClient = useQueryClient();

  const { data: overrides = [] } = useQuery({
    queryKey: ["option-colors", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("option_colors" as any)
        .select("id, option_type, option_value, color")
        .eq("user_id", user.id);
      if (error) throw error;
      return (data || []) as unknown as OptionColor[];
    },
    enabled: !!user?.id,
  });

  // Pre-compute sequential color maps for each type
  const colorMaps = useMemo(() => ({
    vehicle: buildColorMap(activeValues?.vehicle || []),
    expense_type: buildColorMap(activeValues?.expense_type || []),
    paid_by: buildColorMap(activeValues?.paid_by || []),
  }), [activeValues?.vehicle, activeValues?.expense_type, activeValues?.paid_by]);

  const getColor = useCallback(
    (type: "vehicle" | "expense_type" | "paid_by", value: string) => {
      // User override takes priority
      const override = overrides.find(
        (o) => o.option_type === type && o.option_value === value
      );
      if (override) {
        return getOptionColorFromHex(override.color);
      }
      // Sequential assignment from pre-computed map
      const mapped = colorMaps[type]?.get(value);
      if (mapped) return mapped;
      // Fallback (shouldn't normally happen)
      return buildColorMap([value]).get(value)!;
    },
    [overrides, colorMaps]
  );

  const setColor = useMutation({
    mutationFn: async ({
      type,
      value,
      color,
    }: {
      type: "vehicle" | "expense_type" | "paid_by";
      value: string;
      color: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const existing = overrides.find(
        (o) => o.option_type === type && o.option_value === value
      );

      if (existing) {
        const { error } = await supabase
          .from("option_colors" as any)
          .update({ color } as any)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("option_colors" as any)
          .insert({
            user_id: user.id,
            company_id: company?.id || null,
            option_type: type,
            option_value: value,
            color,
          } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["option-colors"] });
    },
  });

  return { getColor, setColor };
}
